import moment from 'moment';
import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
	Badge,
	Button,
	ButtonDropdown,
	Card,
	CardBody,
	CardHeader,
	Col,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Input,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Nav,
	NavItem,
	NavLink,
	Row,
	TabContent,
	TabPane
} from 'reactstrap';
import { AskConfirm } from 'src/components/AskComponents';

import { useQuery } from '@tanstack/react-query';
import Spinner from 'src/components/Spinner';
import graphql from 'src/utils/graphql';
import styled from 'styled-components';
import PlanModal from './PlanModal';
import { DatePicker } from 'reactstrap-date-picker';
import StudentPrint from './StudentPrint';

const StudentCancelled = styled.b`
	position: absolute;
	font-size: 2em;
	color: darkred;
	top: 25%;
	left: 60%;
	transform: rotate(-16deg);
	background: rgba(255, 255, 255, 0.7);
	border: 2px double darkred;
	border-radius: 12px;
	padding: 0px 10px;
	@media (max-width: 520px) {
		left: 35%;
	}
`;

const AvatarContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 15px;
	margin-bottom: 15px;
	
	@media (max-width: 768px) {
		gap: 10px;
	}
	
	@media (max-width: 480px) {
		flex-direction: column;
		align-items: flex-start;
		gap: 10px;
		text-align: center;
	}
`;

const AvatarImage = styled.img`
	width: 80px;
	height: 80px;
	border-radius: 50%;
	object-fit: cover;
	border: 2px solid #dee2e6;
	flex-shrink: 0;
	
	@media (max-width: 768px) {
		width: 70px;
		height: 70px;
	}
	
	@media (max-width: 480px) {
		width: 100px;
		height: 100px;
		align-self: center;
	}
`;

const InfoContainer = styled.div`
	flex: 1;
	min-width: 0;
	
	@media (max-width: 480px) {
		text-align: center;
		width: 100%;
	}
`;

const StudentCard = styled(Card)`
	@media (max-width: 480px) {
		margin-bottom: 20px;
	}
	
	.card-body {
		@media (max-width: 480px) {
			padding: 15px;
		}
	}
`;

const HeaderContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
	
	@media (max-width: 480px) {
		flex-direction: column;
		align-items: flex-start;
		gap: 8px;
	}
`;

const MobileFriendlyDropdown = styled(ButtonDropdown)`
	@media (max-width: 480px) {
		width: 100%;
		
		.dropdown-toggle {
			width: 100%;
			text-align: left;
			justify-content: space-between;
		}
	}
`;

const ActionButtonsContainer = styled.div`
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
	
	@media (max-width: 480px) {
		flex-direction: column;
		width: 100%;
		
		.btn {
			width: 100%;
			margin-bottom: 5px;
		}
	}
`;

const ExperienceBadges = styled.div`
	margin: 10px 0;
	
	.badge {
		margin-bottom: 5px;
		margin-right: 5px;
	}
	
	@media (max-width: 480px) {
		text-align: center;
		
		.badge {
			display: inline-block;
			margin: 2px;
		}
	}
`;

const ContactInfo = styled.div`
	margin: 10px 0;
	
	@media (max-width: 480px) {
		text-align: center;
	}
`;

const StatusMessage = styled.div`
	margin-top: 10px;
	padding: 8px;
	border-radius: 4px;
	font-weight: bold;
	
	@media (max-width: 480px) {
		text-align: center;
		margin: 10px 5px;
	}
`;

const CustomMessageContainer = styled.div`
	margin-top: 10px;
	
	@media (max-width: 480px) {
		margin: 10px 5px;
		
		div {
			margin-left: 15px !important;
		}
	}
`;

const MainActionButtons = styled.div`
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
	margin-top: 1rem;
	
	@media (max-width: 480px) {
		flex-direction: column;
		gap: 8px;
		
		.btn {
			width: 100%;
		}
	}
`;

const PageHeader = styled.h3`
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 15px;
	
	@media (max-width: 768px) {
		flex-direction: column;
		align-items: stretch;
		text-align: center;
	}
`;

const DatePickerContainer = styled.div`
	width: 150px;
	
	@media (max-width: 768px) {
		width: 100%;
		max-width: 200px;
		margin: 0 auto;
	}
`;

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	
	@media (max-width: 768px) {
		justify-content: center;
		flex-wrap: wrap;
	}
`;

const MobileFriendlyNav = styled(Nav)`
	@media (max-width: 480px) {
		flex-direction: column;
		
		.nav-item {
			width: 100%;
		}
		
		.nav-link {
			text-align: center;
			border-radius: 0;
		}
	}
`;

const ActivityGroupHeader = styled.div`
	margin: 20px 0 15px 0;
	
	hr {
		height: 3px;
		margin-bottom: 15px;
	}
	
	h3 {
		margin: 0;
		padding: 10px 0;
		text-align: center;
		background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
		border-radius: 8px;
		border: 1px solid #dee2e6;
		
		@media (max-width: 480px) {
			font-size: 1.2rem;
			padding: 8px 0;
		}
	}
`;
export default function Today() {
	const navigate = useNavigate();
	let { date } = useParams();

	let theDate = () => {
		switch (date) {
			case 'today':
				return moment().format('YYYY-MM-DD');

			case 'tomorrow':
				return moment().add(1, 'day').format('YYYY-MM-DD');

			default:
				return moment(date).format('YYYY-MM-DD');
		}
	};

	const [selectedDate, setSelectedDate] = useState(theDate);

	const updateSelectedDate = (date) => {
		setSelectedDate(date);
		if (date === moment().format('YYYY-MM-DD')) return navigate(`/admin/schedule/today`);
		else if (date === moment().add(1, 'day').format('YYYY-MM-DD')) return navigate(`/admin/schedule/tomorrow`);
		return navigate(`/admin/schedule/${date}`);
	};

	const [activeTab, setActiveTab] = useState('Students');

	const { data, isLoading, error, refetch } = useQuery(
		['schedule', selectedDate],
		async () => {
			const { participation: participations } = await graphql(
				{
					query: `
						query ($date:date!){
							participation(where:{date:{_eq:$date}}) {
								id
								startTime
								startCheckAm
								status {
									id
									name
								}
								person {
									id
									name
									mobile
									dob
									weight
									personHeadSize
									emergencyContactName
									emergencyContactMobile
									avatarObject {
										url
									}
									participations(where:{date:{_lt:$date}, statusId:{_eq:1}}) {
										activity {
											name
											shorthand
										}
									}
								}
								activity {
									name
								}
								pilotUsedRebook
								activityIndex
								pendingDays
								customMessage
							}
						}`,
					variables: {
						date: selectedDate
					}
				},
				true
			);

			return { participations };
		},
		{ cacheTime: 0 }
	);

	const dateHasPassed = moment(selectedDate).isBefore(moment(), 'day');

	const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
	const [reviewData, setReviewData] = useState(null);
	const [weatherData, setWeatherData] = useState([]);
	const [staffData, setStaffData] = useState([]);

	const notifyPilots = async () => {
		try {
			await graphql({
				query: `
				mutation ($date: String!) {
					notifyPilots(date: $date)
				}
			`,
				variables: {
					date: selectedDate
				}
			});
			refetch();
		} catch (error) {
			console.error(error);
		}
	};
	if (isLoading) return <Spinner />;
	const activityGroups = data.participations.reduce((groups, student) => {
		const activityName = student.activity.name;
		if (!groups[activityName]) {
			groups[activityName] = [];
		}
		groups[activityName].push(student);
		return groups;
	}, {});
	return (
		<>
			<PageHeader>
				{moment(selectedDate).format('ll')}
				<HeaderActions>
					<DatePickerContainer>
						<DatePicker
							className="float-right"
							onChange={(e) => {
								updateSelectedDate(e.split('T')[0]);
							}}
							value={selectedDate}
						/>
					</DatePickerContainer>
					<StudentPrint groups={activityGroups} date={selectedDate} />
				</HeaderActions>
				{/* <Input
					className="float-right"
					style={{ width: 150 }}
					value={selectedDate}
					type="date"
					onChange={(e) => {
						setSelectedDate(e.target.value);
					}}
				/> */}
			</PageHeader>

			{isLoading ? (
				<Spinner />
			) : (
				<>
					<MobileFriendlyNav tabs>
						<NavItem>
							<NavLink className={activeTab == 'Students' ? 'active' : ''} onClick={() => setActiveTab('Students')}>
								<i className="fa fa-users" /> Students
							</NavLink>
						</NavItem>
						<NavItem>
							<NavLink className={activeTab == 'Weather' ? 'active' : ''} onClick={() => setActiveTab('Weather')}>
								<i className="fa fa-cloud" /> Weather
							</NavLink>
						</NavItem>
						<NavItem>
							<NavLink className={activeTab == 'Staff' ? 'active' : ''} onClick={() => setActiveTab('Staff')}>
								<i className="fa fa-user-shield" /> Staff
							</NavLink>
						</NavItem>
					</MobileFriendlyNav>
					<TabContent activeTab={activeTab}>
						<TabPane tabId="Students">
							<Row>
								<Col sm="12">
									<Row style={{ marginTop: '1rem' }}>
										<Col sm="12">
											<MainActionButtons>
												{dateHasPassed ? (
													<Button
														color="warning"
														size="sm"
														onClick={() => {
															setIsReviewModalOpen(true);
															setReviewData({ type: 'all' });
														}}
													>
														<i className="fa fa-magnifying-glass" /> Review for everyone
													</Button>
												) : (
													<PlanModal onSubmit={refetch} participations={data.participations} selectedDate={selectedDate}>
														<Button color="warning" size="sm">
															<i className="fa fa-user-clock" /> Set for everyone
														</Button>
													</PlanModal>
												)}
												<AskConfirm
													title="Are you sure you want to notify pilots?"
													description={'Notify for ' + moment(selectedDate).format('ll')}
													onSubmit={() => notifyPilots()}
												>
													<Button color="danger" size="sm">
														<i className="fa fa-comment-dots" /> Finish & Notify Pilots
													</Button>
												</AskConfirm>
											</MainActionButtons>
										</Col>
									</Row>

									{Object.entries(activityGroups).length !== 0 ? (
										Object.entries(activityGroups).map(([activityName, students]) => {
											return (
												<div key={activityName}>
													<ActivityGroupHeader>
														<hr />
														<h3>{activityName}</h3>
													</ActivityGroupHeader>
													<Row style={{ marginTop: '1rem' }}>
														{students.map((student, index) => {
															return (
																<CustomerCard
																	data={student}
																	key={index}
																	dateHasPassed={dateHasPassed}
																	refresh={refetch}
																	isLoading={isLoading}
																	selectedDate={selectedDate}
																	onReview={() => {
																		setIsReviewModalOpen(true);
																		setReviewData(student);
																	}}
																/>
															);
														})}
													</Row>
												</div>
											);
										})
									) : (
										<small>No results</small>
									)}
								</Col>
							</Row>
						</TabPane>
						<TabPane tabId="Weather">
							<Row className="mt-2">
								{weatherData?.hourly?.map((data, index) => {
									let time = moment.unix(data.dt);
									if (time.hour() < 6 || time.hour() > 22) return null;
									if (!time.isSame(selectedDate, 'day')) return null;

									return <WeatherCard data={data} />;
								})}
							</Row>
						</TabPane>
						<TabPane tabId="Staff">
							<Row>
								<Col sm="12">
									<Row style={{ marginTop: '1rem' }}>
										{staffData.length != 0 ? (
											staffData.map((student, index) => {
												return <StaffCard data={student} key={index} />;
											})
										) : (
											<small>No results</small>
										)}
									</Row>
								</Col>
							</Row>
						</TabPane>
					</TabContent>

					<ReviewModal
						isOpen={isReviewModalOpen}
						toggle={() => {
							setIsReviewModalOpen(!isReviewModalOpen);
						}}
						data={reviewData}
					/>
				</>
			)}
		</>
	);
}

const ReviewModal = ({ isOpen, toggle, data }) => {
	if (!data) return null;
	const { id, status, person, activity, activityIndex, pendingDays } = data;

	if (data.type == 'all')
		return (
			<Modal isOpen={isOpen} toggle={toggle}>
				<ModalHeader>
					<i className="fas fa-dove" />
					Review of all!
				</ModalHeader>
				<ModalBody>
					<Input type="select">
						<option>Yes, student was present</option>
						<option>no, student was absent</option>
					</Input>
					<div className="mb-2" />
					Did the student qualify?
					<Input type="select">
						<option>no, more days neccesary</option>
						<option>Yes, they qualified today</option>
					</Input>
					<div className="mb-2" />
					Any notes for next time?
					<Input type="textarea" placeholder="notes..." />
				</ModalBody>
				<ModalFooter>
					<Button>Cancel</Button>
					<Button color="primary">Save</Button>
				</ModalFooter>
			</Modal>
		);

	return (
		<Modal isOpen={isOpen} toggle={toggle}>
			<ModalHeader>
				<i className="fas fa-dove" />
				Review of {person.name}
			</ModalHeader>
			<ModalBody>
				{data && (
					<>
						<div className="mb-3 text-center">
							<div className="mb-2">
								<img 
									src={person.avatarObject?.url || '/no-image.png'} 
									alt="Avatar" 
									style={{ 
										width: '60px', 
										height: '60px', 
										borderRadius: '50%', 
										objectFit: 'cover',
										border: '2px solid #dee2e6'
									}} 
								/>
							</div>
							<h5>{person.name}</h5>
							<h6>
								{activity.name} (day {activityIndex})<br />
								<small>{pendingDays} day(s) pending</small>
							</h6>
							<h6>{person.weight} KG</h6>
						{person.personHeadSize && <h6>Head: {person.personHeadSize} cm</h6>}
							<h6>{moment().diff(moment(person.dob), 'years')} years old</h6>
							<a href={`tel:${person.mobile}`}>
								<i className="fa fa-phone-alt"></i> {person.mobile}
							</a>
							{(person.emergencyContactName || person.emergencyContactMobile) && (
								<h6>
									ICE:{' '}
									{person.emergencyContactName}
									{person.emergencyContactName && person.emergencyContactMobile ? ' — ' : ''}
									{person.emergencyContactMobile && (
										<a href={`tel:${person.emergencyContactMobile}`}>{person.emergencyContactMobile}</a>
									)}
								</h6>
							)}
						</div>
					</>
				)}
				<hr />
				Did the student attend today?
				<Input type="select">
					<option>Yes, student was present</option>
					<option>no, student was absent</option>
				</Input>
				<div className="mb-2" />
				Did the student qualify?
				<Input type="select">
					<option>no, more days neccesary</option>
					<option>Yes, they qualified today</option>
				</Input>
				<div className="mb-2" />
				Any notes for next time?
				<Input type="textarea" placeholder="notes..." />
			</ModalBody>
			<ModalFooter>
				<Button onClick={toggle}>Cancel</Button>
				<Button color="primary">Save</Button>
			</ModalFooter>
		</Modal>
	);
};

const CustomerCard = ({ data, onReview, dateHasPassed, refresh, isLoading, selectedDate }) => {
	const { id, status, person, activity, activityIndex, pilotUsedRebook, startCheckAm, startTime } = data;

	const [isOpen, setIsOpen] = useState(false);
	const [userDropdown, setUserDropdown] = useState(false);
	const toggle = () => setIsOpen(!isOpen);

	return (
		<Col lg={6} md={12} sm={12}>
			<StudentCard>
				<CardHeader>
					<HeaderContainer>
						<MobileFriendlyDropdown
							size="sm"
							isOpen={userDropdown}
							toggle={() => {
								setUserDropdown(!userDropdown);
							}}
						>
						<DropdownToggle caret>
							{person.name}
						</DropdownToggle>
						<DropdownMenu>
							<Link to={`/admin/pilot/${person.id}`}>
								<DropdownItem>
									<i className="fa fa-user" /> View profile
								</DropdownItem>
							</Link>
							{/* <DropdownItem divider />
							<DropdownItem>
								<i className="fa fa-sign-out-alt" /> Pilot Absent
							</DropdownItem> */}
						</DropdownMenu>
					</MobileFriendlyDropdown>
				</HeaderContainer>
				{dateHasPassed ? (
					<ActionButtonsContainer>
						<Button color="warning" size="sm" onClick={onReview}>
							<i className="fa fa-magnifying-glass" /> Review
						</Button>
					</ActionButtonsContainer>
				) : (
					!isLoading && (
						<ActionButtonsContainer>
							<PlanModal onSubmit={refresh} participation={data} selectedDate={selectedDate}>
								<Button color="warning" size="sm">
									<i className="fa fa-user-clock" /> Set start
								</Button>
							</PlanModal>
						</ActionButtonsContainer>
					)
				)}
					{activityIndex === 1 && (
						<Badge color="success" style={{ float: 'right' }}>
							<i className="fa-solid fa-star-of-life" />
							&nbsp; First Day
						</Badge>
					)}
				</CardHeader>
				<CardBody>
					{startCheckAm && (
						<StatusMessage style={{ color: 'red', backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
							<i className="fa fa-fw fa-refresh" /> Status: Please check in the morning
						</StatusMessage>
					)}
					{!!startTime && (
						<StatusMessage style={{ color: 'green', backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
							<i className="fa fa-fw fa-clock" /> Status: Start at&nbsp;
							{moment(0, 'X').utc().startOf('day').add(startTime, 'seconds').format('HH:mm')}
						</StatusMessage>
					)}
					<AvatarContainer>
						<AvatarImage 
							src={person.avatarObject?.url || '/no-image.png'} 
							alt="Avatar" 
						/>
						<InfoContainer>
							<div>
								<i className="fa fa-fw fa-weight" />
								&nbsp;{person.weight} KG&nbsp;&nbsp;
								{person.personHeadSize && <>
									<i className="fa fa-fw fa-hard-hat" />
									&nbsp;{person.personHeadSize} cm&nbsp;&nbsp;
								</>}
								<i className="fa fa-fw fa-calendar" />
								&nbsp;
								{moment().diff(moment(person.dob), 'years')} years old
								{(person.emergencyContactName || person.emergencyContactMobile) && (
									<>
										<br />
										<i className="fa fa-fw fa-phone-alt" />
										&nbsp;ICE: {person.emergencyContactName}
										{person.emergencyContactName && person.emergencyContactMobile ? ' — ' : ''}
										{person.emergencyContactMobile}
									</>
								)}
							</div>
							<b>
								<i className="fa fa-dove"></i> {activity.name} - Day {activityIndex}
							</b>
						</InfoContainer>
					</AvatarContainer>
					<ExperienceBadges>
						<strong>Experience: </strong>
						{Object.entries(
							person.participations.reduce((acc, p) => {
								acc[p.activity.shorthand] = (acc[p.activity.shorthand] || 0) + 1;
								return acc;
							}, {})
						).map(([activity, count], index) => (
							<Badge key={'activity' + index} pill color="success">
								{activity} x{count}
							</Badge>
						))}
					</ExperienceBadges>
					
					<ContactInfo>
						<a href={`tel:${person.mobile}`}>
							<i className="fa fa-phone-alt"></i> {person.mobile}
						</a>
					</ContactInfo>
					
					{pilotUsedRebook && (
						<StatusMessage style={{ color: 'green', backgroundColor: 'rgba(40, 167, 69, 0.1)' }}>
							<i className="fa fa-check" /> Additional day has been credited
						</StatusMessage>
					)}

					{status.id == 2 && !pilotUsedRebook && (
						<StatusMessage style={{ color: 'red', backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
							<i className="fa fa-times" /> Additional day has NOT been credited yet
						</StatusMessage>
					)}

					{data.customMessage && (
						<CustomMessageContainer>
							<i className="fa fa-fw fa-comment" />
							&nbsp;Custom Message:
							<br />
							<div style={{ whiteSpace: 'pre-wrap', overflowX: 'auto', marginLeft: '30px' }}>
								Hi {person.name.split(' ')[0]},<br />
								{data.customMessage}
							</div>
						</CustomMessageContainer>
					)}

					{status.id > 1 && <StudentCancelled>{cancelledMessage(status.id)}</StudentCancelled>}
				</CardBody>
			</StudentCard>
		</Col>
	);
};

const StaffCard = ({ data }) => {
	const { pilot_id, person_first_name, person_last_name, person_mobile, availability_confirmed, availability_note } =
		data;

	return (
		<Col lg={6} md={12} sm={12}>
			<Card>
				<CardHeader>
					<Link to={`/pilot/${pilot_id}`}>
						<i className="fa fa-user-tie" />
						{` ${person_first_name} ${person_last_name}`}
					</Link>
				</CardHeader>
				<CardBody>
					{availability_note ? <div>{availability_note}</div> : null}
					{!availability_confirmed ? <b style={{ color: 'red' }}>UNCONFIRMED</b> : null}
					<br />
					<a href={`tel:${person_mobile}`}>
						<i className="fa fa-phone-alt"></i> {person_mobile}
					</a>
				</CardBody>
			</Card>
		</Col>
	);
};

const WeatherCard = ({ data }) => {
	let m = moment.unix(data.dt);
	return (
		<Col lg={6} md={12} sm={12}>
			<Card className="text-white bg-dark">
				<CardHeader>
					<b style={{ fontSize: 24 }}>{m.format('HH:mm')}</b>
					<span className="float-right">
						<b>{data.weather[0].main}</b> ({data.weather[0].description})
					</span>
				</CardHeader>
				<CardBody>
					<span className="float-right">
						<i className="fa fa-arrow-down" style={{ transform: `rotate(${data.wind_deg}deg)`, fontSize: '4em' }} />
						<br />
						<b>{Math.round(data.wind_speed * 2.237)} mph</b>
					</span>
					<i className="fa fa-cloud" style={{ fontSize: '1.3em' }}>
						{' '}
						{data.clouds}%
					</i>
				</CardBody>
			</Card>
		</Col>
	);
};

const cancelledMessage = (statusId) => {
	if (statusId == 2) return 'CANCELLED (By School)';
	if (statusId == 3) return 'PILOT ABSENT';
	if (statusId == 4) return 'CANCELLED BY PILOT';
	return '';
};
